import { useState, type ReactNode } from 'react';

/**
 * Collapsible section primitive used by every form-detail section.
 *
 * Header: uppercase title + optional count + chevron. Body shown when open.
 * `defaultOpen` controls the initial state per section (noisy ones default
 * collapsed). The whole header is the toggle target — click anywhere on it.
 */
export function SectionCard({
  title,
  count,
  children,
  defaultOpen = true,
  rightSlot,
  emptyHint,
}: {
  title: string;
  count?: number | null;
  children: ReactNode;
  defaultOpen?: boolean;
  rightSlot?: ReactNode;
  emptyHint?: string;
}) {
  const isEmpty = count === 0;
  const [open, setOpen] = useState(defaultOpen && !isEmpty);
  const headerLabel = count != null ? `${title} (${count})` : title;
  return (
    <section className="border border-slate-200 bg-white rounded-lg overflow-hidden shadow-sm">
      <header
        className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none bg-slate-100 hover:bg-slate-200/80 border-b border-slate-200"
        onClick={() => !isEmpty && setOpen((v) => !v)}
        role="button"
        aria-expanded={open}
        tabIndex={isEmpty ? -1 : 0}
        onKeyDown={(e) => {
          if (isEmpty) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <h3 className="text-[11px] uppercase tracking-wider text-brand-danone font-semibold flex-1 truncate">
          {headerLabel}
        </h3>
        {rightSlot}
        {!isEmpty && (
          <Chevron open={open} />
        )}
      </header>
      {open && !isEmpty && (
        <div className="px-4 py-4">{children}</div>
      )}
      {isEmpty && emptyHint && (
        <div className="px-4 py-3 text-sm text-slate-500 italic">
          {emptyHint}
        </div>
      )}
    </section>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
      aria-hidden
    >
      <path
        d="M5 8l5 5 5-5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
